import { useMemo } from "react";

import type { ICampaignPromoModalStackConfig } from "app/components/discountCampaign/campaignScenarioConfig.types";
import { useCampaignDismiss } from "app/components/discountCampaign/common/hooks/useCampaignDismiss";
import type { IDiscountBannerProps } from "app/components/discountCampaign/DiscountBanner/DiscountBanner";
import { useDiscountCampaign } from "app/components/discountCampaign/DiscountBanner/hooks/useDiscountCampaign";
import { buildRegisteredDiscountCampaignConfigs } from "app/marketing/functions/discount-campaign/buildRegisteredDiscountCampaignConfigs";

export interface IModalProps {
    modalConfig: ICampaignPromoModalStackConfig;
    discountEndTime: string;
}

export interface IUseDiscountsApi {
    hasDiscountCampaign: boolean;
    discountBannerProps: IDiscountBannerProps | null;
    discountModalProps: IModalProps | null;
    activeCampaignId: string | null;
}

export const useDiscounts = (): IUseDiscountsApi => {
    const configs = useMemo(() => buildRegisteredDiscountCampaignConfigs(), []);

    // Hooks can't be called from inside .map() over a runtime array, so the registry
    // (always exactly 3 entries, see buildRegisteredDiscountCampaignConfigs) is unrolled
    // into fixed, unconditional top-level calls. Add a new useDiscountCampaign() call here
    // whenever a campaign is registered there.
    const firstCampaign = useDiscountCampaign(configs[0]);
    const secondCampaign = useDiscountCampaign(configs[1]);
    const thirdCampaign = useDiscountCampaign(configs[2]);
    const campaigns = [firstCampaign, secondCampaign, thirdCampaign];

    const activeIndex = campaigns.findIndex((c) => c.shouldShowCampaign);
    const hasDiscountCampaign = activeIndex !== -1;
    const activeConfig = hasDiscountCampaign ? configs[activeIndex] : null;
    const activeCampaign = hasDiscountCampaign ? campaigns[activeIndex] : null;

    // useCampaignDismiss handles a missing storageKey: isOpen follows isEligible
    // and onClose is a no-op. The close button only renders when the banner
    // theme has showCloseButton=true, so the no-op handler never leaks to the UI.
    const { isOpen: isBannerVisible, onClose: onBannerClose } = useCampaignDismiss({
        type: "banner",
        isEligible: hasDiscountCampaign,
        storageKey: activeConfig?.bannerStorageKey,
        trackingPrefix: activeConfig?.bannerTrackingPrefix,
        ctaType: activeConfig?.bannerCtaType,
    });

    const discountBannerProps: IDiscountBannerProps | null =
        isBannerVisible && activeCampaign
            ? {
                  bannerConfig: activeCampaign.bannerConfig,
                  discountEndTime: activeCampaign.discountEndTime,
                  onCtaClick: activeCampaign.onCtaClick,
                  onClose: activeConfig?.bannerStorageKey ? onBannerClose : undefined,
              }
            : null;

    const discountModalProps: IModalProps | null = activeCampaign
        ? {
              modalConfig: activeCampaign.modalConfig,
              discountEndTime: activeCampaign.discountEndTime,
          }
        : null;

    return {
        hasDiscountCampaign,
        discountBannerProps,
        discountModalProps,
        activeCampaignId: hasDiscountCampaign && activeConfig ? activeConfig.id : null,
    };
};
